import { httpClient } from "@/lib/httpClient";
import type { AccessCatalog, UserAccess } from "@/types/access";

/** RBAC admin API. Components never call fetch directly. */
export const accessService = {
  /** Roles + permission catalog for the admin UI. */
  getCatalog: () => httpClient.get<AccessCatalog>("/api/rbac/catalog"),

  /** Every user and pending email grant, with their roles and direct permissions. */
  getUsers: () => httpClient.get<UserAccess[]>("/api/rbac/users"),

  /** Assign a role to an email (pre-provisions if the user has never logged in). */
  assignRole: (email: string, roleKey: string, csrfToken: string) =>
    httpClient.post<{ id: string }>("/api/rbac/assignments", {
      json: { email, roleKey },
      headers: { "x-csrf-token": csrfToken },
    }),

  /** Revoke a role assignment by id. */
  revokeAssignment: (id: string, csrfToken: string) =>
    httpClient.delete<void>(`/api/rbac/assignments/${id}`, {
      headers: { "x-csrf-token": csrfToken },
    }),

  /** Grant a single permission directly to an email. */
  grantPermission: (email: string, permissionKey: string, csrfToken: string) =>
    httpClient.post<{ id: string }>("/api/rbac/grants", {
      json: { email, permissionKey },
      headers: { "x-csrf-token": csrfToken },
    }),

  /** Revoke a direct permission grant by id. */
  revokeGrant: (id: string, csrfToken: string) =>
    httpClient.delete<void>(`/api/rbac/grants/${id}`, {
      headers: { "x-csrf-token": csrfToken },
    }),

  /**
   * Suspend or restore a user's access. Suspension, not deletion: their role
   * assignments and grants stay put underneath, so restoring brings back
   * exactly what they had.
   */
  setBlocked: (userId: string, blocked: boolean, csrfToken: string) =>
    httpClient.put<{ ok: boolean }>(`/api/rbac/users/${userId}/blocked`, {
      json: { blocked },
      headers: { "x-csrf-token": csrfToken },
    }),
};
